import React, { useState, useContext } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography, 
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';

// Google logo SVG component
const GoogleLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Login = ({ open, onClose, message, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { signInWithGoogle } = useAuth();
  const theme = useTheme();

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const user = await signInWithGoogle();
      console.log('Google sign-in successful:', user);
      console.log('Profile photo URL:', user.photoURL);
      
      // Make sure the user object has all required properties
      if (!user.photoURL) {
        console.warn('No photo URL provided in the user object');
      }
      
      // Call onSuccess callback if provided - this will trigger form resubmission
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(user);
        console.log('Called onSuccess with user');
      }
      
      // Use setTimeout to ensure onSuccess effects are completed first
      setTimeout(() => {
        console.log('Closing login dialog after successful authentication');
        onClose();
      }, 200);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={isLoading ? null : onClose}
      PaperProps={{
        sx: {
          borderRadius: 4,
          width: { xs: '90%', sm: '500px' },
          maxWidth: '500px',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.92))',
          backdropFilter: 'blur(10px)',
        },
      }}
    >
      <Box sx={{ position: 'relative', pt: 2, pb: 1 }}>
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontSize: '1.6rem',
          fontWeight: 700,
          pb: 1,
          pt: 2
        }}>
          Sign In to roamzap
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={isLoading}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'text.secondary',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ px: { xs: 3, sm: 4 }, py: 3 }}>
        <Typography variant="body1" sx={{ textAlign: 'center', mb: 4, color: 'text.secondary' }}>
          {message || 'Sign in to unlock personalized trip recommendations, save your itineraries, and sync across devices.'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Stack spacing={2.5} sx={{ mb: 2 }}>
          <Button
            component={motion.button}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            variant="outlined"
            size="large"
            startIcon={<GoogleLogo />}
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: 2.5,
              borderWidth: 2,
              borderColor: 'rgba(0, 0, 0, 0.12)',
              color: 'text.primary',
              fontWeight: 600,
              '&:hover': {
                borderWidth: 2,
                borderColor: 'rgba(0, 0, 0, 0.2)',
                backgroundColor: 'rgba(0, 0, 0, 0.03)',
              },
              fontSize: '1rem',
              textTransform: 'none',
              position: 'relative',
            }}
          >
            {isLoading ? 
              <CircularProgress size={24} sx={{ position: 'absolute', left: 'calc(50% - 12px)' }} /> : 
              'Continue with Google'
            }
          </Button>
        </Stack>
        
        <Box sx={{ mt: 3, mb: 1 }}>
          <Divider />
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 2 
            }}
          >
            By continuing, you agree to our Terms of Service and Privacy Policy.
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Login; 